/**
 * Area Command FIRST-NOTICE decision (#509, UX workflow-completeness).
 *
 * The Area Command layer polls every 45s (AREA_COMMAND_POLL_MS) regardless of layer
 * visibility, reporting the open-Area-Command count + identities to App. Before this
 * cue, an operator learned an Area Command was established ONLY by noticing the
 * layer-picker row count "Area Command (N)" change. This pure helper turns each poll
 * into a first-notice decision: when an Area Command id is seen for the FIRST time this
 * session it names it (or falls back to a generic count when several appear at once),
 * and it SUPPRESSES the boot seed — the first poll on load records the baseline and
 * never toasts pre-existing Area Commands.
 *
 * WHY a grow-only "seen-ever" id set rather than a count-increase gate (adversarial
 * review, #509): the poll's `onCount` fires only on a SUCCESSFUL fetch, but a genuine
 * 200 that transiently omits an open Area Command (page-window under load, read-replica
 * lag) then recovers would, under a count-delta gate, re-announce a PRE-EXISTING Area
 * Command as newly established on recovery (the founder-flagged "transient poll flicker"
 * false positive). Diffing against every id ever seen makes a re-appearing id a no-op
 * (flicker → suppressed) while a genuinely new id still toasts — and it also catches a
 * new establishment that lands in the same poll another Area Command closes (net-flat
 * count). The set is tiny (open Area Commands are rare §4.6 constructs), so grow-only is
 * free. App owns the ref + the showToast side-effect; this stays pure so the transition
 * matrix — boot-seed, first-seen named, several-fresh generic, decrease, no-change,
 * flicker-recovery, churn — is unit-tested deterministically.
 */
import {
  areaCommandEstablishedToast,
  areaCommandEstablishedGenericToast,
} from './incidentVocabulary';

/** Lightweight identity of an open Area Command — id (to diff against everything seen so
 *  far) + the operator-facing name (incidents.name; the Area Command name). Produced by
 *  the layer, forwarded through MapView, consumed here. */
export interface AreaCommandIdentity {
  id: string;
  name: string;
}

/** Every open-Area-Command id observed so far this session (grow-only), or `null` before
 *  the first poll (boot, not yet seeded). A re-appearing id is already in the set, so a
 *  flicker that drops then restores an open Area Command re-adds nothing fresh. */
export type AreaCommandNoticeState = Set<string> | null;

export interface AreaCommandNoticeResult {
  /** The state to store for the next poll: the prior seen-ever set grown by any first-seen
   *  ids in this poll (never shrinks). */
  nextState: Set<string>;
  /** The toast message to fire, or `null` when this poll surfaces no first-seen Area
   *  Command (boot seed, no change, decrease, or a flicker recovery). */
  toast: string | null;
}

/**
 * Decide the first-notice cue for one poll, given every Area Command id seen so far.
 *
 *  - Boot seed (`prior === null`): record every currently-open Area Command as
 *    already-seen, never toast — a pre-existing Area Command open at load is not a
 *    mid-session establishment.
 *  - First-seen ids present: toast. When exactly one Area Command is being seen for the
 *    first time, name it; otherwise (several new at once) fall back to the generic
 *    open-count — never fabricate a name.
 *  - No first-seen ids (no change / decrease / flicker recovery): no toast.
 */
export function computeAreaCommandNotice(
  prior: AreaCommandNoticeState,
  areaCommands: readonly AreaCommandIdentity[],
): AreaCommandNoticeResult {
  // Boot seed — establish the baseline once; pre-existing Area Commands never toast.
  if (prior === null) {
    return { nextState: new Set(areaCommands.map((ac) => ac.id)), toast: null };
  }
  const fresh = areaCommands.filter((ac) => !prior.has(ac.id));
  // Grow-only: the seen-ever set only ever gains the first-seen ids (a flicker that
  // drops an open Area Command never removes its id, so recovery re-adds nothing).
  const nextState = new Set(prior);
  for (const ac of fresh) nextState.add(ac.id);
  if (fresh.length === 0) return { nextState, toast: null };
  const [only] = fresh;
  const toast =
    fresh.length === 1 && only
      ? areaCommandEstablishedToast(only.name)
      : areaCommandEstablishedGenericToast(areaCommands.length);
  return { nextState, toast };
}
